/* ==========================================================================
   The command vocabulary, in one place.

   Both C2 surfaces read from this: the fleet queue (/commands) and the
   per-robot dispatch control (fleet/views/CommandPanel). A status word, a
   hazard class or a parameter's type must mean the same thing on both, or an
   operator who learns one surface has learned nothing about the other.

   Nothing here invents data. The status set, the hazard classes and the
   required-parameter names all come off the API contract
   (`/api/command-profiles`, `/api/commands/*`); what this module adds is the
   *rendering* of them — tone, stage, digest — and the small type table the
   contract does not publish (see PARAM_SPECS).
   ========================================================================== */

import type { Tone } from '../../components/state.ts';
import { NO_VALUE, duration, latlon } from '../../components/format.ts';
import type { Command, CommandStatus } from '../../hooks/useCommands.ts';

/* --------------------------------------------------------------------------
   Status
   -------------------------------------------------------------------------- */

/** The contract's own order: the queue reads top-to-bottom as a pipeline. */
export const CMD_STATUSES: readonly CommandStatus[] = [
  'pending', 'sent', 'acked', 'completed', 'failed', 'cancelled',
];

/**
 * One tone per status, used identically in every place a command appears.
 *
 * `pending` and `cancelled` are deliberately colourless. A queue is mostly
 * pending, and a board where the majority state is saturated has spent its
 * ink on the rows that need nothing; the two that need a human — `failed` and
 * a `sent` that is not acking — are the only ones that carry hue.
 */
export const CMD_TONE: Record<CommandStatus, Tone> = {
  pending: 'stale',
  sent: 'charging',
  acked: 'idle',
  completed: 'good',
  failed: 'error',
  cancelled: 'neutral',
};

/** What the status actually asserts. Reads out on hover and to a reader. */
export const CMD_HINT: Record<CommandStatus, string> = {
  pending: 'Accepted by the cloud, not yet handed to the robot',
  sent: 'Published to the robot; no acknowledgement yet',
  acked: 'The robot acknowledged it and is executing',
  completed: 'The robot reported it finished',
  failed: 'The robot rejected it or it did not finish',
  cancelled: 'Withdrawn by an operator before it completed',
};

/** Statuses that are still the operator's problem. */
export const IN_FLIGHT: ReadonlySet<CommandStatus> = new Set<CommandStatus>([
  'pending', 'sent', 'acked',
]);

export const TERMINAL: ReadonlySet<CommandStatus> = new Set<CommandStatus>([
  'completed', 'failed', 'cancelled',
]);

/* --------------------------------------------------------------------------
   The lifecycle track

   Four slots — issue, dispatch, acknowledge, settle — and per slot exactly
   what we can attest, which is not the same as what we can infer.

   A `completed` command demonstrably passed every stage, so every slot is
   filled. A `failed` or `cancelled` one did NOT tell us where it stopped: the
   contract gives one terminal transition and no intermediate, so those slots
   render `unknown` (hollow, hatched) rather than filled. That distinction is
   the whole point of drawing a track instead of printing a word — a track
   that guessed would be worse than the word.
   -------------------------------------------------------------------------- */

export type Slot = 'done' | 'current' | 'open' | 'unknown' | 'fail' | 'cancel';

export const STAGE_LABELS = ['issue', 'sent', 'ack', 'settle'] as const;

const TRACKS: Record<CommandStatus, readonly Slot[]> = {
  pending: ['current', 'open', 'open', 'open'],
  sent: ['done', 'current', 'open', 'open'],
  acked: ['done', 'done', 'current', 'open'],
  completed: ['done', 'done', 'done', 'current'],
  failed: ['done', 'unknown', 'unknown', 'fail'],
  cancelled: ['done', 'unknown', 'unknown', 'cancel'],
};

export function trackOf(status: CommandStatus): readonly Slot[] {
  return TRACKS[status];
}

/** The sentence the track reads out as, so nothing depends on seeing it. */
export function trackTitle(status: CommandStatus): string {
  const known = status === 'failed' || status === 'cancelled'
    ? ' — no dispatch or acknowledgement transition was recorded, so the middle of the track is unknown'
    : '';
  return `${CMD_HINT[status]}${known}`;
}

/* --------------------------------------------------------------------------
   Hazard classes (`/api/command-profiles`)
   -------------------------------------------------------------------------- */

export type Hazard = 'benign' | 'motion' | 'hazardous' | (string & {});

export const HAZARD_ORDER: readonly string[] = ['benign', 'motion', 'hazardous'];

export function hazardRank(h: string): number {
  const i = HAZARD_ORDER.indexOf(h);
  return i === -1 ? HAZARD_ORDER.length : i;
}

/**
 * Hazard class → tone.
 *
 * Amber is NOT available here. In this console amber means "not current, or
 * approaching a limit", and it is also the accent that marks the primary verb
 * — so painting the motion class amber would put two ambers a centimetre from
 * the amber SEND button and devalue both. A hazard class is a classification,
 * not an alarm: `motion` reads informational (blue), `benign` is unremarkable
 * (grey, and draws no edge bar at all), and red is kept for the one class the
 * profile flags above motion, which on a koko is a cutting head.
 */
export function hazardTone(h: string): Tone {
  if (h === 'hazardous') return 'error';
  if (h === 'motion') return 'ok';
  if (h === 'benign') return 'neutral';
  return 'error'; /* a class the agent invented later is never the safe one */
}

/**
 * What the class asserts, in the contract's own terms and no further. The
 * profile says `motion`; it does not say what the robot will do, and a
 * console that embroiders that has started writing fiction about a machine.
 */
export function hazardConsequence(h: string): string {
  if (h === 'benign') return 'does not command motion';
  if (h === 'motion') return 'commands motion — the robot will drive or actuate';
  if (h === 'hazardous') return 'hazardous class — the profile flags this above motion';
  return `hazard class '${h}' — not one of the three the contract publishes`;
}

/** One entry of a robot's published command profile. */
export interface ProfileVerb {
  name: string;
  hazard: string;
  required: readonly string[];
}

/**
 * Read the profile off a robot row. `command_profile` is typed as an opaque
 * bag in the shared hook, so the shape is checked here rather than asserted.
 * A robot that has never enrolled carries `null` and gets `null` back — not
 * an empty list, which would read as "this robot accepts nothing".
 */
export function readProfile(profile: Record<string, unknown> | null | undefined): ProfileVerb[] | null {
  if (profile === null || profile === undefined) return null;
  const raw = profile['commands'];
  if (typeof raw !== 'object' || raw === null) return null;
  const out: ProfileVerb[] = [];
  for (const [name, spec] of Object.entries(raw as Record<string, unknown>)) {
    const s = (typeof spec === 'object' && spec !== null ? spec : {}) as Record<string, unknown>;
    const req = Array.isArray(s['required_params'])
      ? (s['required_params'] as unknown[]).filter((v): v is string => typeof v === 'string')
      : [];
    out.push({
      name,
      hazard: typeof s['hazard'] === 'string' ? s['hazard'] : 'unknown',
      required: req,
    });
  }
  out.sort((a, b) => hazardRank(a.hazard) - hazardRank(b.hazard) || a.name.localeCompare(b.name));
  return out;
}

export function profileVersion(profile: Record<string, unknown> | null | undefined): string | null {
  if (profile === null || profile === undefined) return null;
  const v = profile['version'];
  return typeof v === 'string' ? v : null;
}

/* --------------------------------------------------------------------------
   Parameters

   THE GAP, STATED HONESTLY: `/api/command-profiles` publishes the *names* of
   a verb's required parameters and nothing else — no type, no unit, no range,
   no default. A typed composer needs all four, so this table supplies them
   for the parameter names the contract actually uses, and every name NOT in
   it falls back to a type inferred from a value the robot's own history
   already carries (see specFor). Nothing is invented for a name we have
   neither a spec nor an observation for: it renders as a free string and says
   so.

   The fix belongs on the server — a `param_schema` beside `required_params`.
   -------------------------------------------------------------------------- */

export type ParamKind = 'number' | 'text' | 'bool' | 'latlon' | 'json';

export interface ParamSpec {
  kind: ParamKind;
  unit?: string;
  step?: number;
  min?: number;
  max?: number;
  placeholder?: string;
  /** Where the console's knowledge of this field comes from. */
  source: 'contract' | 'observed' | 'unknown';
}

const KNOWN: Record<string, Omit<ParamSpec, 'source'>> = {
  waypoint: { kind: 'latlon' },
  mission_id: { kind: 'text', placeholder: 'MSN-2474' },
  speed_mps: { kind: 'number', unit: 'm/s', step: 0.05, min: 0, max: 4 },
  label: { kind: 'text', placeholder: 'row-14' },
  resolution_m: { kind: 'number', unit: 'm', step: 0.01, min: 0.01, max: 1 },
  loop_closure: { kind: 'bool' },
  target_height_m: { kind: 'number', unit: 'm', step: 0.1, min: 0 },
  limb_id: { kind: 'text', placeholder: 'L4' },
  max_diameter_mm: { kind: 'number', unit: 'mm', step: 1, min: 0 },
};

/** The type to edit `name` as, given a value the history has shown for it. */
export function specFor(name: string, observed: unknown): ParamSpec {
  const known = KNOWN[name];
  if (known !== undefined) return { ...known, source: 'contract' };
  if (typeof observed === 'number') return { kind: 'number', source: 'observed' };
  if (typeof observed === 'boolean') return { kind: 'bool', source: 'observed' };
  if (typeof observed === 'string') return { kind: 'text', source: 'observed' };
  if (observed !== null && typeof observed === 'object') {
    const o = observed as Record<string, unknown>;
    if (typeof o['lat'] === 'number' && typeof o['lon'] === 'number') {
      return { kind: 'latlon', source: 'observed' };
    }
    return { kind: 'json', source: 'observed' };
  }
  return { kind: 'text', source: 'unknown' };
}

/** One `key value` pair, formatted the way the console formats that shape. */
export function formatParam(key: string, value: unknown): string {
  if (value === null || value === undefined) return NO_VALUE;
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') {
    const unit = KNOWN[key]?.unit;
    return unit === undefined ? String(value) : `${value} ${unit}`;
  }
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>;
    if (typeof o['lat'] === 'number' && typeof o['lon'] === 'number') {
      return latlon(o['lat'], o['lon']);
    }
    return JSON.stringify(value);
  }
  return String(value);
}

/** `waypoint 45.52341, −122.67 · speed 0.71 m/s` — a row's worth of params. */
export function paramDigest(params: Record<string, unknown> | null | undefined): string {
  if (params === null || params === undefined) return '';
  const keys = Object.keys(params);
  if (keys.length === 0) return '';
  return keys.map((k) => `${k} ${formatParam(k, params[k])}`).join(' · ');
}

/* --------------------------------------------------------------------------
   Results and failures
   -------------------------------------------------------------------------- */

export interface Failure {
  code: string | null;
  message: string;
}

/** The failure a `failed` command's result payload carries, or null. */
export function failureOf(result: Record<string, unknown> | null | undefined): Failure | null {
  if (result === null || result === undefined) return null;
  const err = result['error'];
  if (typeof err !== 'string') return null;
  const code = result['code'];
  return { code: typeof code === 'string' ? code : null, message: err };
}

/**
 * The single most informative string this command carries, for the queue's
 * one free column: why it failed, who cancelled it, what it produced, or what
 * it was asked to do.
 */
export function detailOf(cmd: {
  status: CommandStatus;
  params: Record<string, unknown>;
  result: Record<string, unknown> | null;
}): { mark: string | null; text: string } {
  const fail = failureOf(cmd.result);
  if (fail !== null) {
    /* The CODE is the fault's class and takes the red; the message is the
       detail and stays dim. Fourteen failed rows each printing a full
       sentence in --error-fg is a wall of red with no hierarchy in it. */
    return { mark: fail.code, text: fail.message };
  }
  if (cmd.status === 'cancelled') {
    const by = cmd.result?.['cancelled_by'];
    return { mark: null, text: typeof by === 'string' ? `cancelled by ${by}` : 'cancelled' };
  }
  if (cmd.status === 'completed' && cmd.result !== null) {
    const parts = Object.entries(cmd.result)
      .filter(([k]) => k !== 'ok')
      .map(([k, v]) => `${k} ${formatParam(k, v)}`);
    if (parts.length > 0) return { mark: null, text: parts.join(' · ') };
  }
  return { mark: null, text: paramDigest(cmd.params) };
}

/* --------------------------------------------------------------------------
   Latency

   Two instants exist per command in the contract: `issued_at`, and
   `updated_at` for the last transition. The difference is the only latency
   the platform can honestly report, and it is reported as what it is — issue
   to the state the command is in now — never as a generic "response time".
   -------------------------------------------------------------------------- */

export function latencyMs(cmd: Pick<Command, 'status' | 'issued_at' | 'updated_at'>): number | null {
  if (cmd.status === 'pending') return null;
  const a = Date.parse(cmd.issued_at);
  const b = Date.parse(cmd.updated_at);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return null;
  return b - a;
}

/** Buckets for the latency histogram. Fixed edges, so the shape is stable. */
export const LATENCY_BUCKETS: readonly { key: string; label: string; max: number }[] = [
  { key: 'b0', label: '< 5s', max: 5_000 },
  { key: 'b1', label: '5–15s', max: 15_000 },
  { key: 'b2', label: '15–30s', max: 30_000 },
  { key: 'b3', label: '30–60s', max: 60_000 },
  { key: 'b4', label: '1–2m', max: 120_000 },
  { key: 'b5', label: '2–5m', max: 300_000 },
  { key: 'b6', label: '> 5m', max: Number.POSITIVE_INFINITY },
];

export function bucketOf(ms: number): string {
  for (const b of LATENCY_BUCKETS) if (ms < b.max) return b.key;
  return 'b6';
}

/** Percentile over a sorted array. Returns null for an empty sample. */
export function percentile(sorted: readonly number[], p: number): number | null {
  if (sorted.length === 0) return null;
  const i = Math.min(sorted.length - 1, Math.max(0, Math.round((sorted.length - 1) * p)));
  return sorted[i] ?? null;
}

/** `1m 12s` or the em dash — never `0` for "we have no sample". */
export function latencyText(ms: number | null): string {
  return ms === null ? NO_VALUE : duration(ms);
}

/* --------------------------------------------------------------------------
   The threshold ladder

   THE POINT OF THIS MODULE'S LAST SECTION: the board already knew its own
   p50 and p95 and printed them in three places — the header strip, the facet
   rail's histogram and the flow plot's dashed rule — and then rendered every
   duration cell in one flat ink, so a row sitting at the p95 was the dimmest
   thing on screen and a command acked 23 hours ago was typographically
   identical to one acked two seconds ago. A threshold the operator has to
   hold in their head is not a threshold.

   So the same two numbers come back down into the rows. Four bands, and the
   edges are the window's own measurement wherever there is enough of it:

     nominal   under the median               --text-mid
     high      median → p95                   --text-hi
     warn      at or past p95                 --warn   + a triangle mark
     over      past the ceiling               --error  + a block mark + tint

   Two ceilings, because the two columns measure different things:

     LATENCY is a settled measurement — issue to the state it reached. Its
     ceiling is the latency histogram's own top bucket edge (5m), so the row
     ink and the `> 5m` bar in the rail agree by construction.

     WAIT (time in state) is unfinished work. Its ceiling is 15m, past which
     a command is not slow, it is abandoned. And it only applies to the three
     in-flight statuses: for a completed or cancelled command "time in state"
     is how long ago it settled, which is history, not rot, and history is
     dim (`null` band) so live work is the only thing carrying ink.

   Every edge is published on the surface (see the ISSUE → STATE block in the
   facet rail), never only in this file.
   -------------------------------------------------------------------------- */

export type Band = 'nominal' | 'high' | 'warn' | 'over';

export interface Ladder {
  /** Median issue→state latency over the window's settled commands, ms. */
  p50: number;
  /** 95th percentile, ms. */
  p95: number;
  /** A settled dispatch past this is an outlier: the histogram's top edge. */
  tail: number;
  /** Unsettled work past this is not slow, it is stuck. */
  stuck: number;
  /** How many settled commands the percentiles were measured over. */
  n: number;
  /** False when the sample was too thin to measure and the fixed edges hold. */
  measured: boolean;
}

/**
 * Below this many settled commands the percentiles are noise — three samples
 * put the "p95" wherever the slowest of the three landed, and the whole
 * column would recolour every time one more row settled. Under it the ladder
 * falls back to fixed edges and says so.
 */
const MIN_SAMPLE = 8;

/** Fixed edges, used when the window is too thin to measure its own. */
const FIXED_P50 = 30_000;
const FIXED_P95 = 120_000;

/** The latency histogram's top bucket edge — `> 5m`. */
export const TAIL_MS = 300_000;
/** In flight and untouched for this long: stuck, not slow. */
export const STUCK_MS = 900_000;

/** Build the ladder from a sorted sample of settled latencies. */
export function ladderOf(sorted: readonly number[]): Ladder {
  const measured = sorted.length >= MIN_SAMPLE;
  const p50 = measured ? (percentile(sorted, 0.5) ?? FIXED_P50) : FIXED_P50;
  const p95 = measured ? (percentile(sorted, 0.95) ?? FIXED_P95) : FIXED_P95;
  return {
    /* p95 can equal p50 on a very flat window; keeping the bands ordered
       stops a single value from being both "high" and "warn". */
    p50,
    p95: Math.max(p95, p50),
    tail: TAIL_MS,
    stuck: STUCK_MS,
    n: sorted.length,
    measured,
  };
}

function band(ms: number, p50: number, p95: number, ceiling: number): Band {
  if (ms >= ceiling) return 'over';
  if (ms >= p95) return 'warn';
  if (ms >= p50) return 'high';
  return 'nominal';
}

/** The band a settled command's latency falls in. Null when it has none. */
export function latencyBand(ms: number | null, l: Ladder): Band | null {
  return ms === null ? null : band(ms, l.p50, l.p95, l.tail);
}

/**
 * The band a command's time-in-state falls in.
 *
 * Null for the three terminal statuses: that duration is how long ago the
 * command settled, and a completed command is not rotting because it
 * finished yesterday.
 */
export function waitBand(ms: number, status: CommandStatus, l: Ladder): Band | null {
  if (!IN_FLIGHT.has(status)) return null;
  return band(ms, l.p50, l.p95, l.stuck);
}

/** The band of the longest wait in a set — the group header's question. */
export function worstBand(bands: readonly (Band | null)[]): Band | null {
  const order: Band[] = ['nominal', 'high', 'warn', 'over'];
  let best: Band | null = null;
  for (const b of bands) {
    if (b === null) continue;
    if (best === null || order.indexOf(b) > order.indexOf(best)) best = b;
  }
  return best;
}

/** What a band asserts, in the window's own numbers. Reads out on hover. */
export function bandNote(b: Band | null, l: Ladder, kind: 'latency' | 'wait'): string {
  const src = l.measured
    ? `issue → settle p50 ${duration(l.p50)} · p95 ${duration(l.p95)}, over the ${l.n} commands in this window that finished`
    : `fixed edges — only ${l.n} command${l.n === 1 ? '' : 's'} in this window have finished, too few to measure a percentile`;
  if (b === null) {
    return kind === 'latency'
      ? 'Still in flight — nothing has settled, so there is no measurement'
      : 'Settled: this is how long ago it finished, not how long it has waited';
  }
  const ceil = kind === 'latency' ? l.tail : l.stuck;
  const what = kind === 'latency' ? 'took' : 'has waited';
  if (b === 'over') {
    return kind === 'latency'
      ? `Past ${duration(ceil)} — outside the histogram's top bucket · ${src}`
      : `In flight and untouched for over ${duration(ceil)} — stuck, not slow · ${src}`;
  }
  if (b === 'warn') return `At or past p95 — ${what} longer than 95% of this window's commands take to finish · ${src}`;
  if (b === 'high') return `Between p50 and p95 · ${src}`;
  return `Under the median · ${src}`;
}
